import {
  ConstructionCompanyDetailForm,
  ConstructionCompanyRegistrationForm,
} from '../../types/company.types';
import { DepartmentResponse } from '../../types';
import { CreateConstructionCompanyRequest } from '../../services/companies.service';

import { mapRepresentativesToDepartmentContactLists } from './common-mapper';

/**
 * Common interface for construction company form data fields used in mapping
 */
interface ConstructionCompanyFormFields {
  galiCustomerCode?: string;
  officialName: string;
  abbreviatedName: string;
  nameKatakana: string;
  representatives: Array<{
    department: string;
    salesPerson: { id: string; firstName: string; lastName: string; code: string };
  }>;
}

/**
 * Shared helper function to map construction company form data to API request format
 * Converts form field names to API field names and IDs to codes
 */
const mapConstructionCompanyFormToRequest = (
  formData: ConstructionCompanyFormFields,
  departments: DepartmentResponse[] = []
): CreateConstructionCompanyRequest => {
  const departmentContactLists = mapRepresentativesToDepartmentContactLists(
    formData.representatives,
    departments
  );

  return {
    galiAccountCode: formData.galiCustomerCode ? Number.parseInt(formData.galiCustomerCode) : null,
    name: formData.officialName,
    nameAbbreviation: formData.abbreviatedName,
    nameKana: formData.nameKatakana || '',
    departmentContactLists,
  };
};

/**
 * Maps company registration form data to API request format
 * Converts form field names to API field names and IDs to codes
 */
export const mapConstructionCompanyFormRequest = (
  formData: ConstructionCompanyRegistrationForm,
  departments: DepartmentResponse[] = []
): CreateConstructionCompanyRequest => {
  return mapConstructionCompanyFormToRequest(formData, departments);
};

/**
 * Type alias for edit request (same structure as create request)
 */
export type EditConstructionCompanyRequest = CreateConstructionCompanyRequest;

/**
 * Maps construction company detail form data to API request format for editing
 * Converts form field names to API field names and IDs to codes
 */
export const mappingEditConstructionCompanyData = (
  formData: ConstructionCompanyDetailForm,
  departments: DepartmentResponse[] = []
): EditConstructionCompanyRequest => {
  // Extract only the fields needed for mapping (excluding isCommonCement which is not in the request)
  const formFields: ConstructionCompanyFormFields = {
    galiCustomerCode: formData.galiCustomerCode,
    officialName: formData.officialName,
    abbreviatedName: formData.abbreviatedName,
    nameKatakana: formData.nameKatakana,
    representatives: formData.representatives,
  };
  return mapConstructionCompanyFormToRequest(formFields, departments);
};
